import MlsDataConverter from '@/converters/3.6/MlsDataConverter';
import IAddress1UnitNoZipCode from '@/models/common/IAddress1UnitNoZipCode';
import Address1UnitNoZipCodeUtils from '@/models/common/Address1UnitNoZipCodeUtils';
import CompAddressUtils from '@/models/comp/CompAddressUtils';
import ICompAddress from '@/models/CompAddress';
import IFeedData from '@/models/comp/IFeedData';
import IFeedDatas from '@/models/comp/IFeedDatas';
import IMlsCsvFileInfo from '@/models/mls/IMlsCsvFileInfo';
import IMlsCsvFileInfos from '@/models/mls/IMlsCsvFileInfos';
import IMlsImportStats, { newMlsImportStats } from '@/models/mls/IMlsImportStats';
import IMLSPropertyV1 from '@/models/mls/IMLSPropertyV1';
import MlsPropertyDataUtils from '@/models/mls/MlsPropertyDataUtils';
import MlsPropertyItemUtils from '@/models/mls/MlsPropertyItemUtils';
import IMLSPropertySource from '@/models/mls/IMLSPropertySource';
import IMLSPropertyItem from '@/models/mls/IMLSPropertyItem';
import IMLSPropertyData from '@/models/mls/IMLSPropertyData';
import utils from '@/utils/QSUtils';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import IMLSPhotos from '@/models/mls/IMLSPhotos';

// Internal structures - payload types for actions
//
interface IPropertyItemIndexed {
    index: number;
    propertyItem: IMLSPropertyItem;
}

interface IFeedDataIndexed {
    index: number;
    feedData: IFeedData;
}

interface IMediaUrlsEntry {
    address1UnitNoZipCode: IAddress1UnitNoZipCode;
    mediaUrls: IMLSPhotos[];
}

interface IMediaUrlsIndexed {
    index: number;
    entry: IMediaUrlsEntry;
}

// Internal only: The set of mutation method names as strings.
// Actions call mutations via this.context.commit() with the first argument as the mutation method name.
const Mutate = {
    addProperty: 'addProperty',
    replaceProperty: 'replaceProperty',
    deleteProperty: 'deleteProperty',
    addCsvFileInfo: 'addCsvFileInfo',
    addFeedData: 'addFeedData',
    replaceFeedData: 'replaceFeedData',
    deleteFeedData: 'deleteFeedData',
    addMediaUrlsEntry: 'addMediaUrlsEntry',
    replaceMediaUrlsEntry: 'replaceMediaUrlsEntry',
};

/**
 * An MLS Store implemented using vuex-module-decorators.
 * https://github.com/championswimmer/vuex-module-decorators
 * https://championswimmer.in/vuex-module-decorators/pages/overview.html
 *
 * Getters at the top, Mutators at the bottom.
 * All Actions have a summary indicating they're an action (intellisense purposes).
 * All Actions are async since a Promise is implemented internally.
 */
@Module({ namespaced: true, name: 'mlsV36' })
export default class MlsStoreModuleV36 extends VuexModule {
    // Private are the store properties
    private mlsProperties: IMLSPropertyData = [];
    private mlsFeedDatas: IFeedDatas = [];
    private mlsMediaUrlsEntries: IMediaUrlsEntry[] = [];
    private csvFileInfos: IMlsCsvFileInfos = [];
    private importStats: IMlsImportStats = newMlsImportStats();

    /**
     * Get the MLS property data
     */
    get MlsProperties(): IMLSPropertyData {
        return this.mlsProperties;
    }

    /**
     * Get the MLS feed data (new, already-UAD-shaped payload from ConvertMLSMapperDataToUAD36).
     */
    get MlsFeedDatas(): IFeedDatas {
        return this.mlsFeedDatas;
    }

    get MlsFeedDataByAddress1UnitNoZipCode() {
        return (address1UnitNoZipCode: IAddress1UnitNoZipCode): IFeedData | null => {
            const idx = getFeedDataIndexByAddress1UnitNoZipCode(this.mlsFeedDatas, address1UnitNoZipCode);
            return idx >= 0 ? this.mlsFeedDatas[idx] : null;
        };
    }

    get HasFeedDatas(): boolean {
        return this.mlsFeedDatas.length > 0;
    }

    /**
     * Get MLS media URLs (additional photos) captured client-side from the raw MLS Mapper payload for the new
     * processAndStore2 pipeline, by address. Returns an empty array when none found.
     */
    get MlsMediaUrlsByAddress1UnitNoZipCode() {
        return (address1UnitNoZipCode: IAddress1UnitNoZipCode): IMLSPhotos[] => {
            const idx = getMediaUrlsIndexByAddress1UnitNoZipCode(this.mlsMediaUrlsEntries, address1UnitNoZipCode);
            return idx >= 0 ? this.mlsMediaUrlsEntries[idx].mediaUrls : [];
        };
    }

    get MlsPropertyByAddress1UnitNoZipCode() {
        return (address1UnitNoZipCode: IAddress1UnitNoZipCode): IMLSPropertyItem | null => {
            const propertyItem = MlsPropertyDataUtils.getByAddress1UnitNoAndZipCode(this.mlsProperties, address1UnitNoZipCode);
            return propertyItem;
        };
    }

    get MlsPropertyByAddress() {
        return (compAddress: ICompAddress): IMLSPropertyItem | null => {
            const address1UnitNoZipCode = CompAddressUtils.getAddress1UnitNoAndZipCode(compAddress);
            const propertyItem = MlsPropertyDataUtils.getByAddress1UnitNoAndZipCode(this.mlsProperties, address1UnitNoZipCode);
            return propertyItem;
        };
    }

    get MlsPropertySourceByAddressAndFormFieldName() {
        return (compAddress: ICompAddress, fieldName: string): IMLSPropertySource[] | null | undefined => {
            let result: IMLSPropertySource[] | null | undefined;
            const address1UnitNoZipCode = CompAddressUtils.getAddress1UnitNoAndZipCode(compAddress);
            const propertyItem = MlsPropertyDataUtils.getByAddress1UnitNoAndZipCode(this.mlsProperties, address1UnitNoZipCode);
            if (propertyItem) {
                result = MlsPropertyDataUtils.getPropertySourceByFormFieldName(propertyItem, fieldName);
            }
            return result;
        };
    }

    get MlsPropertySourceByAddressAndParentFormFieldName() {
        return (compAddress: ICompAddress, fieldName: string): IMLSPropertySource[] | null | undefined => {
            let result: IMLSPropertySource[] | null | undefined;
            const address1UnitNoZipCode = CompAddressUtils.getAddress1UnitNoAndZipCode(compAddress);
            const propertyItem = MlsPropertyDataUtils.getByAddress1UnitNoAndZipCode(this.mlsProperties, address1UnitNoZipCode);
            if (propertyItem) {
                result = MlsPropertyDataUtils.getPropertySourceByParentFormFieldName(propertyItem, fieldName);
            }
            return result;
        };
    }

    /**
     * Attempts to get a MLS Property (in V1 format) using the given comp address.
     */
    get MlsPropertyFieldValueByAddress() {
        // This is how to implement a getter with arguments
        //
        return (compAddress: ICompAddress): IMLSPropertyV1 | null => {
            let result: IMLSPropertyV1 | null = null;

            const address1UnitNoZipCode = CompAddressUtils.getAddress1UnitNoAndZipCode(compAddress);
            const propertyItem = MlsPropertyDataUtils.getByAddress1UnitNoAndZipCode(this.mlsProperties, address1UnitNoZipCode);
            if (propertyItem) {
                result = MlsDataConverter.convertSingleToV1(propertyItem);
            }
            return result;
        };
    }

    get MlsPropertyAdditionalPhotoCountByAddress() {
        return (compAddress: ICompAddress): number => {
            let result = 0;
            const address1UnitNoZipCode = CompAddressUtils.getAddress1UnitNoAndZipCode(compAddress);
            const propertyItem = MlsPropertyDataUtils.getByAddress1UnitNoAndZipCode(this.mlsProperties, address1UnitNoZipCode);
            if (propertyItem?.mediaUrls) {
                result = utils.objectSize(propertyItem.mediaUrls);
            }
            return result;
        };
    }

    get MlsPropertyAdditionalPhotosByAddress() {
        return (compAddress: ICompAddress): IMLSPhotos[] => {
            let result: any = [];
            const address1UnitNoZipCode = CompAddressUtils.getAddress1UnitNoAndZipCode(compAddress);
            const propertyItem = MlsPropertyDataUtils.getByAddress1UnitNoAndZipCode(this.mlsProperties, address1UnitNoZipCode);
            if (propertyItem?.mediaUrls) {
                result = propertyItem.mediaUrls;
            }
            return result;
        };
    }

    /**
     * Gets the array of MLS CSV file infos.
     */
    get CsvFileInfos(): IMlsCsvFileInfos {
        return this.csvFileInfos;
    }

    /**
     * Get the latest import stats
     */
    get MlsImportStats(): IMlsImportStats {
        return this.importStats;
    }

    get HasProperties(): boolean {
        return this.mlsProperties.length > 0;
    }

    /**
     * Add or replace the given MLS property item to the store's MLS property data.
     * @param propertyItem
     * @returns true on add, false on replace
     */
    @Action
    async addOrReplaceProperty(propertyItem: IMLSPropertyItem): Promise<boolean> {
        let result = false;
        const matchIdx = MlsPropertyDataUtils.getIndexByPropertyItem(this.mlsProperties, propertyItem);
        if (matchIdx >= 0) {
            const replacePayload: IPropertyItemIndexed = {
                index: Number(matchIdx),
                propertyItem: propertyItem,
            };
            this.context.commit(Mutate.replaceProperty, replacePayload);
        } else {
            this.context.commit(Mutate.addProperty, propertyItem);
            result = true;
        }
        return result;
    }

    @Action
    async removeProperty(propertyItem: IMLSPropertyItem): Promise<boolean> {
        let result = false;
        const matchIdx = MlsPropertyDataUtils.getIndexByPropertyItem(this.mlsProperties, propertyItem);
        if (matchIdx >= 0) {
            this.context.commit(Mutate.deleteProperty, matchIdx);
            result = true;
        }
        return result;
    }

    /**
     * Add or replace the given MLS feed data (new shape) by address.
     * @param feedData
     * @returns true on add, false on replace
     */
    @Action
    async addOrReplaceFeedData(feedData: IFeedData): Promise<boolean> {
        let result = false;
        const address1UnitNoZipCode = MlsPropertyItemUtils.getAddress1UnitNoAndZipCode36(feedData);
        const matchIdx = getFeedDataIndexByAddress1UnitNoZipCode(this.mlsFeedDatas, address1UnitNoZipCode);
        if (matchIdx >= 0) {
            const replacePayload: IFeedDataIndexed = {
                index: Number(matchIdx),
                feedData: feedData,
            };
            this.context.commit(Mutate.replaceFeedData, replacePayload);
        } else {
            this.context.commit(Mutate.addFeedData, feedData);
            result = true;
        }
        return result;
    }

    @Action
    async removeFeedData(feedData: IFeedData): Promise<boolean> {
        let result = false;
        const address1UnitNoZipCode = MlsPropertyItemUtils.getAddress1UnitNoAndZipCode36(feedData);
        const matchIdx = getFeedDataIndexByAddress1UnitNoZipCode(this.mlsFeedDatas, address1UnitNoZipCode);
        if (matchIdx >= 0) {
            this.context.commit(Mutate.deleteFeedData, matchIdx);
            result = true;
        }
        return result;
    }

    /**
     * Add or replace the MLS media URLs (additional photos), captured client-side from the raw MLS Mapper
     * payload, for the given address - used by the new processAndStore2 pipeline.
     * @param entry
     */
    @Action
    async addOrReplaceMediaUrls(entry: IMediaUrlsEntry): Promise<void> {
        const matchIdx = getMediaUrlsIndexByAddress1UnitNoZipCode(this.mlsMediaUrlsEntries, entry.address1UnitNoZipCode);
        if (matchIdx >= 0) {
            const replacePayload: IMediaUrlsIndexed = {
                index: matchIdx,
                entry: entry,
            };
            this.context.commit(Mutate.replaceMediaUrlsEntry, replacePayload);
        } else {
            this.context.commit(Mutate.addMediaUrlsEntry, entry);
        }
    }

    /**
     * Mutation:
     * @param propertyItem
     */
    @Mutation
    addProperty(propertyItem: IMLSPropertyItem) {
        this.mlsProperties.push(propertyItem);
    }

    /**
     * Mutation:
     * @param replacePayload
     */
    @Mutation
    replaceProperty(replacePayload: IPropertyItemIndexed) {
        this.mlsProperties[replacePayload.index] = replacePayload.propertyItem;
    }

    /**
     * Mutation:
     * @param index
     */
    @Mutation
    deleteProperty(index: number) {
        if (index < this.mlsProperties.length) {
            this.mlsProperties.splice(index, 1);
        }
    }

    /**
     * Mutation:
     * @param fileInfo
     */
    @Mutation
    addCsvFileInfo(fileInfo: IMlsCsvFileInfo) {
        this.csvFileInfos.push(fileInfo);
    }

    /**
     * Mutation:
     * @param feedData
     */
    @Mutation
    addFeedData(feedData: IFeedData) {
        this.mlsFeedDatas.push(feedData);
    }

    /**
     * Mutation:
     * @param replacePayload
     */
    @Mutation
    replaceFeedData(replacePayload: IFeedDataIndexed) {
        this.mlsFeedDatas[replacePayload.index] = replacePayload.feedData;
    }

    /**
     * Mutation:
     * @param index
     */
    @Mutation
    deleteFeedData(index: number) {
        if (index < this.mlsFeedDatas.length) {
            this.mlsFeedDatas.splice(index, 1);
        }
    }

    /**
     * Mutation:
     * @param entry
     */
    @Mutation
    addMediaUrlsEntry(entry: IMediaUrlsEntry) {
        this.mlsMediaUrlsEntries.push(entry);
    }

    /**
     * Mutation:
     * @param replacePayload
     */
    @Mutation
    replaceMediaUrlsEntry(replacePayload: IMediaUrlsIndexed) {
        this.mlsMediaUrlsEntries[replacePayload.index] = replacePayload.entry;
    }

    /**
     * Mutation:
     * @param fileInfo
     */
    @Mutation
    setImportStats(importStats: IMlsImportStats) {
        this.importStats = importStats;
    }

    /**
     * Mutation:
     */
    @Mutation
    resetState() {
        this.mlsProperties = [];
        this.mlsFeedDatas = [];
        this.mlsMediaUrlsEntries = [];
        this.csvFileInfos = [];
        this.importStats = newMlsImportStats();
    }
}

/**
 * Find the index of the feed data matching the given address1, unit number and zip code.
 * @param feedDatas
 * @param address1UnitNoZipCode
 * @returns the matching index, or -1 when not found
 */
function getFeedDataIndexByAddress1UnitNoZipCode(feedDatas: IFeedDatas, address1UnitNoZipCode: IAddress1UnitNoZipCode): number {
    return feedDatas.findIndex((feedData) => Address1UnitNoZipCodeUtils.matches(MlsPropertyItemUtils.getAddress1UnitNoAndZipCode36(feedData), address1UnitNoZipCode));
}

/**
 * Find the index of the media URLs entry matching the given address1, unit number and zip code.
 * @param entries
 * @param address1UnitNoZipCode
 * @returns the matching index, or -1 when not found
 */
function getMediaUrlsIndexByAddress1UnitNoZipCode(entries: IMediaUrlsEntry[], address1UnitNoZipCode: IAddress1UnitNoZipCode): number {
    return entries.findIndex((entry) => Address1UnitNoZipCodeUtils.matches(entry.address1UnitNoZipCode, address1UnitNoZipCode));
}
